import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
// Firebase imports removed
// import { collection, addDoc, updateDoc, doc } from 'firebase-shim';
// import { db } from '../../firebase/config';
import indexedDBService from '../../services/IndexedDBService';

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  gstin?: string;
  userId: string;
  createdAt: any;
  state?: string;
  defaultItems?: Array<{
    id?: string;
    inventoryItemId?: string;
    name: string;
    quantity: number;
    price: number;
  }>;
  isZeroClickEnabled?: boolean;
  zeroClickFrequency?: string;
  autoSendEmail?: boolean;
}

interface NewClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (client: Client) => void;
  client: Client | null;
  userId: string;
}

const NewClientDialog: React.FC<NewClientDialogProps> = ({
  open,
  onClose,
  onSave,
  client,
  userId,
}) => {
  const [formData, setFormData] = useState<Omit<Client, 'id' | 'userId' | 'createdAt'>>({
    name: '',
    email: '',
    phone: '',
    address: '',
    gstin: '',
    state: '',
    defaultItems: [],
    isZeroClickEnabled: false,
    zeroClickFrequency: 'Monthly',
    autoSendEmail: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const inventoryData = await indexedDBService.getAllItems<any>('inventory');
        setInventoryItems(inventoryData);
      } catch (error) {
        console.error('Error fetching inventory:', error);
      }
    };
    fetchInventory();
  }, []);

  // Reset form when dialog opens or client changes
  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        gstin: client.gstin || '',
        state: client.state || '',
        defaultItems: client.defaultItems || [],
        isZeroClickEnabled: client.isZeroClickEnabled || false,
        zeroClickFrequency: client.zeroClickFrequency || 'Monthly',
        autoSendEmail: client.autoSendEmail || false,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        gstin: '',
        state: '',
        defaultItems: [],
        isZeroClickEnabled: false,
        zeroClickFrequency: 'Monthly',
        autoSendEmail: false,
      });
    }
    setErrors({});
  }, [client, open]);

  const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const processed = name === 'gstin' ? value.toUpperCase() : value;
    setFormData(prev => ({
      ...prev,
      [name]: processed,
    }));

    // Live GSTIN validation
    if (name === 'gstin') {
      if (processed && !GSTIN_REGEX.test(processed)) {
        setErrors(prev => ({ ...prev, gstin: 'Invalid GSTIN. Format: 22AAAAA0000A1Z5' }));
      } else {
        setErrors(prev => { const n = { ...prev }; delete n.gstin; return n; });
      }
      return;
    }

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Client name is required';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }

    if (formData.gstin && !GSTIN_REGEX.test(formData.gstin.toUpperCase())) {
      newErrors.gstin = 'Invalid GSTIN format. Expected: 22AAAAA0000A1Z5';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      defaultItems: [...(prev.defaultItems || []), { id: Date.now().toString(), name: '', quantity: 1, price: 0 }]
    }));
  };

  const handleRemoveItem = (index: number) => {
    setFormData(prev => {
      const newItems = [...(prev.defaultItems || [])];
      newItems.splice(index, 1);
      return { ...prev, defaultItems: newItems };
    });
  };

  const handleItemChange = (index: number, field: string, value: string | number) => {
    setFormData(prev => {
      const newItems = [...(prev.defaultItems || [])];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, defaultItems: newItems };
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      let savedClient: Client;

      if (client) {
        // Update existing client in IndexedDB
        // await updateDoc(doc(db, 'clients', client.id), { ... });

        savedClient = {
          ...client,
          ...formData,
          updatedAt: new Date().toISOString(),
        } as unknown as Client;

        // Update in IndexedDB
        try {
          // Use generic updateItem if available or saveClient
          await indexedDBService.putItem('clients', savedClient);
        } catch (indexedDBError) {
          console.warn('Error updating client in IndexedDB:', indexedDBError);
        }
      } else {
        // Create new client in IndexedDB
        const newClientData = {
          ...formData,
          userId,
          createdAt: new Date().toISOString(),
        };

        // Save to IndexedDB
        // const docRef = await addDoc(collection(db, 'clients'), newClientData);
        let newId = 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

        savedClient = {
          id: newId,
          ...newClientData,
        } as Client;

        try {
          await indexedDBService.putItem('clients', savedClient);
        } catch (indexedDBError) {
          console.warn('Error saving client to IndexedDB:', indexedDBError);
        }
      }

      onSave(savedClient);
    } catch (error) {
      console.error('Error saving client:', error);
      // Handle error (could add toast notification here)
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {client ? 'Edit Client' : 'Add New Client'}
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Client Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="GSTIN (optional)"
                name="gstin"
                value={formData.gstin || ''}
                onChange={handleInputChange}
                error={!!errors.gstin}
                helperText={errors.gstin || '15-character format: 22AAAAA0000A1Z5'}
                inputProps={{ maxLength: 15, style: { textTransform: 'uppercase' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                name="state"
                value={formData.state || ''}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle2" color="primary">Invoicing Automation</Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                {(formData.defaultItems || []).map((item, index) => (
                  <Grid container spacing={2} key={item.id} sx={{ mb: 2, alignItems: 'center' }}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        freeSolo
                        options={inventoryItems}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                        value={inventoryItems.find(inv => inv.id === item.inventoryItemId) || item.name}
                        onChange={(e, newValue) => {
                          setFormData(prev => {
                            const newItems = [...(prev.defaultItems || [])];
                            if (typeof newValue === 'string') {
                              newItems[index] = { ...newItems[index], name: newValue, inventoryItemId: undefined };
                            } else if (newValue) {
                              newItems[index] = { 
                                ...newItems[index], 
                                name: newValue.name, 
                                price: newValue.price, 
                                inventoryItemId: newValue.id 
                              };
                            } else {
                              newItems[index] = { ...newItems[index], name: '', inventoryItemId: undefined };
                            }
                            return { ...prev, defaultItems: newItems };
                          });
                        }}
                        onInputChange={(e, newInputValue) => {
                          handleItemChange(index, 'name', newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Product/Service Name"
                            placeholder="e.g. Monthly Retainer"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Price"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton color="error" onClick={() => handleRemoveItem(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={handleAddItem}
                  variant="outlined" 
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Default Item
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isZeroClickEnabled || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isZeroClickEnabled: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Enable Zero-Click Invoicing"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Frequency"
                name="zeroClickFrequency"
                value={formData.zeroClickFrequency || 'Monthly'}
                onChange={handleInputChange}
                SelectProps={{ native: true }}
                disabled={!formData.isZeroClickEnabled}
              >
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Yearly">Yearly</option>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoSendEmail || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, autoSendEmail: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Auto-Send Invoice via Email (1-Click & Zero-Click)"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewClientDialog; 
